import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { Setting } from '@/types';
import { useState } from 'react';

interface CompanyDataEditorProps {
    setting: Setting;
    onUpdate: (key: string, value: CompanyData) => void;
}

interface CompanyData {
    name: string;
    address: string;
    company_code: string;
    vat_number: string;
    phone: string;
    email: string;
}

export default function CompanyDataEditor({ setting, onUpdate }: CompanyDataEditorProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState<CompanyData>(() => {
        const currentValue = setting.value as unknown as CompanyData;
        return {
            name: currentValue?.name || '',
            address: currentValue?.address || '',
            company_code: currentValue?.company_code || '',
            vat_number: currentValue?.vat_number || '',
            phone: currentValue?.phone || '',
            email: currentValue?.email || '',
        };
    });

    const handleSave = () => {
        onUpdate(setting.key, formData);
        setIsEditing(false);
    };

    const handleCancel = () => {
        const currentValue = setting.value as unknown as CompanyData;
        setFormData({
            name: currentValue?.name || '',
            address: currentValue?.address || '',
            company_code: currentValue?.company_code || '',
            vat_number: currentValue?.vat_number || '',
            phone: currentValue?.phone || '',
            email: currentValue?.email || '',
        });
        setIsEditing(false);
    };

    const updateField = (field: keyof CompanyData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const formatDisplayValue = (data: CompanyData) => {
        const fields = Object.entries(data).filter(([value]) => value && value.trim() !== '');
        return fields.length > 0 ? `${fields.length} fields configured` : 'Not configured';
    };

    if (!isEditing) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-muted-foreground text-sm">Default company information for invoice generation</p>
                        <p className="text-sm font-medium">{formatDisplayValue(formData)}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                        Edit
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                    <Label htmlFor="name">Company Name</Label>
                    <Input id="name" value={formData.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Enter company name" />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="company_code">Company Code</Label>
                    <Input
                        id="company_code"
                        value={formData.company_code}
                        onChange={(e) => updateField('company_code', e.target.value)}
                        placeholder="Enter company code"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="vat_number">VAT Number</Label>
                    <Input
                        id="vat_number"
                        value={formData.vat_number}
                        onChange={(e) => updateField('vat_number', e.target.value)}
                        placeholder="Enter VAT number"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        placeholder="Enter phone number"
                    />
                </div>

                <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        placeholder="Enter email address"
                    />
                </div>

                <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="address">Address</Label>
                    <Textarea
                        id="address"
                        value={formData.address}
                        onChange={(e) => updateField('address', e.target.value)}
                        placeholder="Enter company address"
                        rows={3}
                    />
                </div>
            </div>

            <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={handleCancel}>
                    Cancel
                </Button>
                <Button onClick={handleSave}>Save</Button>
            </div>
        </div>
    );
}
